<mat-card class="analytics-card">
  <mat-card-header>
    <mat-card-title>Fallback Analytics Overview</mat-card-title>
    <mat-card-subtitle
      >Pick a custom date range to see voice-to-text fallback trends and
      automated insights</mat-card-subtitle
    >
  </mat-card-header>

  <mat-card-content>
    <div class="date-range">
      <mat-form-field appearance="outline">
        <mat-label>Start Date</mat-label>
        <input matInput [matDatepicker]="pickerStart" [(ngModel)]="startDate" />
        <mat-datepicker-toggle
          matIconSuffix
          [for]="pickerStart"
        ></mat-datepicker-toggle>
        <mat-datepicker #pickerStart></mat-datepicker>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>End Date</mat-label>
        <input matInput [matDatepicker]="pickerEnd" [(ngModel)]="endDate" />
        <mat-datepicker-toggle
          matIconSuffix
          [for]="pickerEnd"
        ></mat-datepicker-toggle>
        <mat-datepicker #pickerEnd></mat-datepicker>
      </mat-form-field>

      <button
        mat-raised-button
        color="primary"
        (click)="fetchOverview()"
        [disabled]="isLoading() || !startDate || !endDate"
      >
        <mat-icon *ngIf="!isLoading()">insights</mat-icon>
        <mat-spinner diameter="20" *ngIf="isLoading()"></mat-spinner>
        {{ isLoading() ? 'Loading...' : 'Analyze' }}
      </button>
    </div>

    <div class="error" *ngIf="errorMessage()">
      <mat-icon>error_outline</mat-icon>
      <p>{{ errorMessage() }}</p>
    </div>

    <ng-container *ngIf="hasSearched() && !isLoading() && !errorMessage()">
      <p class="time-range-note" *ngIf="timeRangeLabel()">
        {{ timeRangeLabel() }}
      </p>

      <div class="summary-grid">
        <div class="summary-card">
          <div class="summary-label">Voice Sessions</div>
          <div class="summary-value">{{ totalVoiceSessions() }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Fell Back to Text</div>
          <div class="summary-value">{{ totalFallbacks() }}</div>
        </div>
        <div class="summary-card highlight">
          <div class="summary-label">Fallback Rate</div>
          <div class="summary-value">
            {{ overallFallbackRatePct() | number: '1.0-1' }}%
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Voice-Only Rate</div>
          <div class="summary-value">{{ voiceOnlyRate() }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Avg Session Duration</div>
          <div class="summary-value">{{ avgSessionDuration() }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Avg Time to Fallback</div>
          <div class="summary-value">{{ avgTimeToFallback() }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Keepalive Rate</div>
          <div class="summary-value">
            {{ overallKeepaliveRatePct() | number: '1.0-1' }}%
          </div>
        </div>
      </div>

      <div class="insights" *ngIf="insights().length > 0">
        <h3>Automated Insights</h3>
        <div
          class="insight-card"
          *ngFor="let insight of insights()"
          [ngClass]="insight.severity"
        >
          <mat-icon>{{
            insight.severity === 'warning' ? 'warning' : 'lightbulb'
          }}</mat-icon>
          <div class="insight-body">
            <div class="insight-title">{{ insight.title }}</div>
            <div class="insight-description">{{ insight.description }}</div>
            <div class="insight-action">
              <strong>Action:</strong> {{ insight.action }}
            </div>
          </div>
        </div>
      </div>

      <div class="distribution" *ngIf="fallbackDistribution().length > 0">
        <h3>Fallback Path</h3>
        <div class="distribution-grid">
          <div
            class="distribution-item"
            *ngFor="let row of fallbackDistribution()"
          >
            <div class="distribution-count">{{ row.count }}</div>
            <div class="distribution-label">{{ row.label }}</div>
          </div>
        </div>
      </div>

      <div class="breakdown" *ngIf="byProductType().length > 0">
        <h3>By Product Type</h3>
        <table mat-table [dataSource]="byProductType()" class="breakdown-table">
          <ng-container matColumnDef="label">
            <th mat-header-cell *matHeaderCellDef>Product Type</th>
            <td mat-cell *matCellDef="let row">{{ row.label }}</td>
          </ng-container>
          <ng-container matColumnDef="sessions">
            <th mat-header-cell *matHeaderCellDef>Voice Sessions</th>
            <td mat-cell *matCellDef="let row">{{ row.total }}</td>
          </ng-container>
          <ng-container matColumnDef="fallbacks">
            <th mat-header-cell *matHeaderCellDef>Fallbacks</th>
            <td mat-cell *matCellDef="let row">{{ row.fallbacks }}</td>
          </ng-container>
          <ng-container matColumnDef="rate">
            <th mat-header-cell *matHeaderCellDef>Fallback Rate</th>
            <td mat-cell *matCellDef="let row">
              <span class="rate" [class.outlier]="row.isOutlier">
                {{ row.fallbackRatePct | number: '1.0-1' }}%
              </span>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="breakdownColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: breakdownColumns"></tr>
        </table>
      </div>

      <div class="breakdown" *ngIf="byBrand().length > 0">
        <h3>By Brand</h3>
        <table mat-table [dataSource]="byBrand()" class="breakdown-table">
          <ng-container matColumnDef="label">
            <th mat-header-cell *matHeaderCellDef>Brand</th>
            <td mat-cell *matCellDef="let row">{{ row.label }}</td>
          </ng-container>
          <ng-container matColumnDef="sessions">
            <th mat-header-cell *matHeaderCellDef>Voice Sessions</th>
            <td mat-cell *matCellDef="let row">{{ row.total }}</td>
          </ng-container>
          <ng-container matColumnDef="fallbacks">
            <th mat-header-cell *matHeaderCellDef>Fallbacks</th>
            <td mat-cell *matCellDef="let row">{{ row.fallbacks }}</td>
          </ng-container>
          <ng-container matColumnDef="rate">
            <th mat-header-cell *matHeaderCellDef>Fallback Rate</th>
            <td mat-cell *matCellDef="let row">
              <span class="rate" [class.outlier]="row.isOutlier">
                {{ row.fallbackRatePct | number: '1.0-1' }}%
              </span>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="breakdownColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: breakdownColumns"></tr>
        </table>
      </div>

      <div class="breakdown" *ngIf="byDate().length > 0">
        <h3>By Date</h3>
        <table mat-table [dataSource]="byDate()" class="breakdown-table">
          <ng-container matColumnDef="label">
            <th mat-header-cell *matHeaderCellDef>Date</th>
            <td mat-cell *matCellDef="let row">{{ row.label }}</td>
          </ng-container>
          <ng-container matColumnDef="sessions">
            <th mat-header-cell *matHeaderCellDef>Voice Sessions</th>
            <td mat-cell *matCellDef="let row">{{ row.total }}</td>
          </ng-container>
          <ng-container matColumnDef="fallbacks">
            <th mat-header-cell *matHeaderCellDef>Fallbacks</th>
            <td mat-cell *matCellDef="let row">{{ row.fallbacks }}</td>
          </ng-container>
          <ng-container matColumnDef="rate">
            <th mat-header-cell *matHeaderCellDef>Fallback Rate</th>
            <td mat-cell *matCellDef="let row">
              <span class="rate" [class.outlier]="row.isOutlier">
                {{ row.fallbackRatePct | number: '1.0-1' }}%
              </span>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="breakdownColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: breakdownColumns"></tr>
        </table>
      </div>
    </ng-container>

    <div
      class="empty"
      *ngIf="!hasSearched() && !isLoading() && !errorMessage()"
    >
      <mat-icon>date_range</mat-icon>
      <p>Select a date range and click "Analyze" to see fallback trends.</p>
    </div>
  </mat-card-content>
</mat-card>
